import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlignItemsProps } from 'app/components/complex/DatePickerFilter';
import { EvtKeys } from 'app/types';

import Container, { DialogSize } from './Container';
import DialogBanner, { DialogBannerProps } from './DialogBanner';
import Overlay from './Overlay';
import styles, { BorderRadius, overlay } from '../../constants/styles';
import BodyEnd from '../BodyEnd';
import Box from '../Box';
import KeyCodeListener from '../EventListeners/KeyCodeListener';
import IconButton from '../IconButton';

// Local Typings
export interface DialogProps {
  alignItems?: AlignItemsProps;
  animateIn?: boolean;
  animateOut?: boolean;
  bannerProps?: DialogBannerProps;
  borderRadius?: BorderRadius;
  isFull?: boolean;
  isFullScreen?: boolean;
  isOpen: boolean;
  isOverflowYVisible?: boolean;
  onClose?: () => void;
  onExited?: () => void;
  shouldCloseOnClickOutside?: boolean;
  size?: DialogSize;
  withCloseButton?: boolean;
  withFocusTrap?: boolean;
  withTransition?: boolean;
}

// Local Variables
const topOffset = -100;
const transition = 300;

// Component Definition
const Dialog: FC<PropsWithChildren<DialogProps>> = ({
  animateIn = true,
  animateOut = true,
  onExited,
  shouldCloseOnClickOutside = true,
  withFocusTrap = true,
  ...props
}) => {
  const { t } = useTranslation('components');
  const [bgColor, setBgColor] = useState(overlay);
  const [opacity, setOpacity] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [top, setTop] = useState(topOffset);

  function handleClose() {
    props.onClose?.();
  }

  function handleClickOutside() {
    if (shouldCloseOnClickOutside) {
      handleClose();
    }
  }

  const handleClosingTransition = useCallback(() => {
    if (isClosing) {
      setTimeout(() => {
        setIsClosing(false);
        onExited?.();
      },
      transition);
    }
  },
  [isClosing, onExited]);

  useEffect(() => {
    setBgColor(props.isOpen
      ? styles.backgrounds.modal
      : styles.backgrounds.clearBlack);
    setOpacity(props.isOpen ? 1 : 0);
    setTop(props.isOpen ? 0 : topOffset);
    if (props.isOpen) {
      setIsClosing(true);
    } else {
      handleClosingTransition();
    }
  },
  [handleClosingTransition, props.isOpen]);

  const CloseButton = !props.bannerProps && props.withCloseButton && props.onClose
    ? (
      <Box
        className="dialog_close-button-container"
        position="absolute"
        right={6}
        top={6}
      >
        <IconButton
          ariaLabel={t('dialog.closeAriaLabel', 'activate to close dialog')}
          circle
          className="dialog_close-button"
          iconName="close"
          iconSize="xxs"
          onClick={handleClose}
        />
      </Box>
    )
    : undefined;

  const shouldAnimate = props.isOpen ? animateIn : animateOut;

  return props.isOpen || isClosing
    ? (
      <BodyEnd>
        <KeyCodeListener
          keyCodeActions={[
            { action: handleClose, key: EvtKeys.Esc },
          ]}
        >
          <Overlay
            bgColor={bgColor}
            disablePadding={props.isFullScreen}
            transition={shouldAnimate ? transition : 0}
          >
            <Container
              alignItems={props.alignItems}
              borderRadius={props.borderRadius}
              CloseButton={CloseButton}
              isActive={withFocusTrap}
              isFull={props.isFull}
              isFullScreen={props.isFullScreen}
              isOpen={props.isOpen}
              onClickOutside={handleClickOutside}
              opacity={opacity}
              overflowYVisible={props.isOverflowYVisible}
              size={props.size ?? 'md'}
              top={top}
              transition={shouldAnimate ? transition : 0}
              withTransition={props.withTransition}
            >
              {props.bannerProps && <DialogBanner {...props.bannerProps} />}

              {props.children}
            </Container>
          </Overlay>
        </KeyCodeListener>
      </BodyEnd>
    )
    : null;
};

export default Dialog;
